/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `capabilities` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class CapabilitiesMesg : Mesg {
    public constructor() : super("Capabilities", Profile.MesgNum.CAPABILITIES)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** Number of values stored in languages. */
    public val numLanguages: Int
        get() = getField(LANGUAGES_FIELD_NUM)?.numValues ?: 0

    /** languages. Use language_bits_x types where x is index of array. */
    public var languages: List<UByte?>?
        get() {
            val field = getField(LANGUAGES_FIELD_NUM) ?: return null
            return List(field.numValues) { getLanguages(it) }
        }
        set(value) {
            removeField(LANGUAGES_FIELD_NUM)
            value?.forEachIndexed { index, element -> setLanguages(index, element) }
        }

    public fun getLanguages(index: Int): UByte? =
        getFieldValue(LANGUAGES_FIELD_NUM, index) as? UByte

    public fun setLanguages(index: Int, value: UByte?) {
        setFieldValue(LANGUAGES_FIELD_NUM, value, index)
    }

    /** Number of values stored in sports. */
    public val numSports: Int
        get() = getField(SPORTS_FIELD_NUM)?.numValues ?: 0

    /** sports. Use sport_bits_x types where x is index of array. */
    public var sports: List<SportBits0?>?
        get() {
            val field = getField(SPORTS_FIELD_NUM) ?: return null
            return List(field.numValues) { getSports(it) }
        }
        set(value) {
            removeField(SPORTS_FIELD_NUM)
            value?.forEachIndexed { index, element -> setSports(index, element) }
        }

    public fun getSports(index: Int): SportBits0? =
        (getFieldValue(SPORTS_FIELD_NUM, index) as? UByte)?.let { SportBits0.fromValue(it) }

    public fun setSports(index: Int, value: SportBits0?) {
        setFieldValue(SPORTS_FIELD_NUM, value?.value, index)
    }

    /** workouts_supported. */
    public var workoutsSupported: WorkoutCapabilities?
        get() = (getFieldValue(WORKOUTS_SUPPORTED_FIELD_NUM) as? UInt)?.let { WorkoutCapabilities.fromValue(it) }
        set(value) = setFieldValue(WORKOUTS_SUPPORTED_FIELD_NUM, value?.value)

    /** connectivity_supported. */
    public var connectivitySupported: ConnectivityCapabilities?
        get() = (getFieldValue(CONNECTIVITY_SUPPORTED_FIELD_NUM) as? UInt)?.let { ConnectivityCapabilities.fromValue(it) }
        set(value) = setFieldValue(CONNECTIVITY_SUPPORTED_FIELD_NUM, value?.value)

    public companion object {
        public const val LANGUAGES_FIELD_NUM: UByte = 0u
        public const val SPORTS_FIELD_NUM: UByte = 1u
        public const val WORKOUTS_SUPPORTED_FIELD_NUM: UByte = 21u
        public const val CONNECTIVITY_SUPPORTED_FIELD_NUM: UByte = 23u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[0] = Field("Languages", 0u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[1] = Field("Sports", 1u, BaseType.UINT8Z, 1.0, 0.0, "", false)
            fields[21] = Field("WorkoutsSupported", 21u, BaseType.UINT32Z, 1.0, 0.0, "", false)
            fields[23] = Field("ConnectivitySupported", 23u, BaseType.UINT32Z, 1.0, 0.0, "", false)
            return fields
        }
    }
}
